import {useActiveShiftFocus} from "../../hooks/useActiveShiftFocus";
import {useDispatch} from "react-redux";
import {useAppSelector} from "../../store/hooks/useAppSelector";
import {getAppDttm} from "../../store/ui/selectors";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {setAppDate} from "../../store/ui/actions";
import {
  addDays,
  adjustToTimeZone,
  endOfDay,
  shiftsSelector,
  useSearchShiftsQuery,
  formatISO,
  getObjectId,
  startOfDay,
  subDays,
  TransportAssocEntity, transportCategory,
  ttasSelector,
  useGetDispatchTtasByDayQuery,
  useTtaPrefetch
} from "@ots-frontend/ots-data";
import {LoadingOverlay} from "@mantine/core";
import {TicketWorkspace} from "@ots-frontend/ots-ui-tw";
import {useNavigate} from "react-router-dom";
import {toFormDefaultsFromTta} from "../DispatchFormPage/DispatchFormPage";
import {
  isDefaultTicketFilter,
  GridSort,
  ResizeableContent,
  ThemedPage,
  TicketDataGrid,
  TicketDataGridContextMenuItem,
  TicketFilterFormModal,
  TicketFilterFormValues,
  useApp,
  useTicketAction,
  useTicketFormPageNavigate,
  useTtaMutation,
} from "@ots-frontend/ots-ui";
import {skipToken} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDisclosure} from "@mantine/hooks";
import {CompactSelection, DataEditorRef, GridSelection, Item} from "@glideapps/glide-data-grid";
import {getTicketPageFilter, getTicketPageState} from "../../store/ticket-page/selectors";
import {CellClickedEventArgs} from "@glideapps/glide-data-grid/dist/ts/data-grid/data-grid-types";
import {
  setTicketPageFilter,
  setTicketPageStateCell, setTicketPageStateOilCell, setTicketPageStateOilSort, setTicketPageStateOilWidths,
  setTicketPageStateSort,
  setTicketPageStateWidths
} from "../../store/ticket-page/actions";

export type TicketPageProps = Record<string, never>

export const TicketPage = () => {

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {appCategory, regionId} = useApp();
  const {t} = useTranslation()

  const appDttm = useAppSelector(getAppDttm);
  const filter = useAppSelector(getTicketPageFilter)
  const pageState = useAppSelector(getTicketPageState)

  const dataEditorRef = useRef<DataEditorRef>(null);
  const [gridControlsTarget, setGridControlsTarget] = useState<HTMLDivElement | null>(null);

  const [filterOpened, filterHandler] = useDisclosure(false);
  const [showSearch, setShowSearch] = useState(false);
  const onSearchClose = useCallback(() => setShowSearch(false), []);

  const [selection, setSelection] = useState<GridSelection>({
    columns: CompactSelection.empty(),
    rows: CompactSelection.empty()
  });

  const gridState = useMemo(() => {
    return appCategory === transportCategory.oil ? pageState.oil : pageState.standard
  }, [appCategory, pageState])

  const [colWidths, setColWidths] = useState<(number | undefined)[]>(gridState.widths)
  const [sort, setSort] = useState<GridSort | undefined>(gridState.sort)

  const prefetchTtas = useTtaPrefetch("getDispatchTtasByDay");

  const selectTtas = useMemo(() => {
    return ttasSelector()
  }, []);

  const queryArg = useMemo(() => {
    if (regionId) {
      return {
        day: formatISO(startOfDay(appDttm)),
        regionId: regionId,
      }
    } else {
      return skipToken
    }
  }, [appDttm, regionId])

  const {ttas, isFetching, refetch,} = useGetDispatchTtasByDayQuery(queryArg, {
    pollingInterval: 120000,
    selectFromResult: (result) => ({
      ...result,
      ttas: selectTtas(result, {
        regionId: regionId,
        category: appCategory,
        personId: getObjectId(filter.person),
        vehicleId: getObjectId(filter.vehicle),
        trailerId: getObjectId(filter.trailer),
        pickupSiteId: getObjectId(filter.pickupSite),
        pickupSiteCompanyId: getObjectId(filter.pickupSiteCompany),
        deliverySiteId: getObjectId(filter.deliverySite),
        deliverySiteCompanyId: getObjectId(filter.deliverySiteCompany),
        adminValidated: filter.adminValidated,
        dispatchValidated: filter.dispatchValidated,
        fieldValidated: filter.fieldValidated,
        businessValidated: filter.businessValidated,
      }),
      isFetching: result.isFetching,
    })
  });

  const driverFocus = useActiveShiftFocus(regionId, dataEditorRef, isFetching);

  const selectShiftEntities = useMemo(() => shiftsSelector(), []);
  const shiftQueryArg = useMemo(() => regionId ? {
    fromDttm: adjustToTimeZone(subDays(startOfDay(appDttm), 1)).getTime(),
    toDttm: adjustToTimeZone(addDays(endOfDay(appDttm), 1)).getTime(),
    regionIds: [regionId],
  } : skipToken, [appDttm, regionId]);
  const {shifts, refetch: refetchShifts} = useSearchShiftsQuery(shiftQueryArg, {
    pollingInterval: 120000,
    selectFromResult: result => ({...result, shifts: selectShiftEntities(result, {dateSort: true})}),
  });

  const prefetchDttmRange = useCallback((date: Date) => {
    const rootDate = startOfDay(date);

    if (regionId) {
      // prefetch the previous day
      prefetchTtas({
        day: formatISO(subDays(rootDate, 1)),
        regionId: regionId
      });

      // prefetch the next day
      prefetchTtas({
        day: formatISO(addDays(rootDate, 1)),
        regionId: regionId
      });
    }

  }, [prefetchTtas, regionId]);

  const initialGridCell = useRef(gridState?.cell);
  const restoreInitialCell = useRef(true);
  useEffect(() => {
    const cell = initialGridCell.current;
    if (driverFocus.personId) {restoreInitialCell.current = false; return;}
    if (!restoreInitialCell.current || !cell) return;
    const timer = window.setTimeout(() => {
      restoreInitialCell.current = false;
      dataEditorRef.current?.scrollTo(0, cell[1], "both", 0, 0, {vAlign: "start", hAlign: "start"});
      setSelection(previous => ({...previous, current: {
        cell: [0, cell[1]], range: {x: 0, y: cell[1], width: 1, height: 1}, rangeStack: [],
      }}));
    }, 200);
    return () => window.clearTimeout(timer);
  }, [driverFocus.personId]);

  const onDateChange = useCallback((date: Date) => {
    dispatch(setAppDate(date.getTime()));
    prefetchDttmRange(date);
  }, [dispatch, prefetchDttmRange]);

  const {onTicketActionClick} = useTicketAction()
  const {dispatchValidateAll} = useTtaMutation()
  const {navigate: ticketFormNavigate} = useTicketFormPageNavigate()

  const onItemClick = useCallback((item: TicketDataGridContextMenuItem, data: TransportAssocEntity[]) => {
    if (item === "VIEW") {
      navigate(`/tickets/profile?id=${data[0].id}`)
    } else if (item === "VIEW_DISPATCH") {
      navigate(`/dispatch/profile?id=${data[0].dispatchId}`)
    } else if (item === "NEW_DISPATCH") {
      navigate("/dispatch/form", {
        state: {
          formDefaults: toFormDefaultsFromTta(data[0])
        }
      });
    } else if (item === "ADD_TRANSPORT") {
      const transport = data[0].pickupTransport ?? data[0].deliveryTransport;
      if (transport) {
        ticketFormNavigate(transport)
      }
    } else if (item === "CLEAR" || item === "COMMENT" || item === "COMMENT_DELIVERY") {
      return;
    } else if (item === "DISPATCH_VALIDATE") {
      dispatchValidateAll(data, true, undefined)
    } else if (item === "DISPATCH_INVALIDATE") {
      dispatchValidateAll(data, false, undefined)
    } else {
      onTicketActionClick(data[0], item)
    }
  }, [navigate, onTicketActionClick, ticketFormNavigate, dispatchValidateAll]);

  const onFilterSubmit = useCallback((filter: TicketFilterFormValues) => {
    dispatch(setTicketPageFilter(filter))
    filterHandler.close()
  }, [dispatch, filterHandler])

  const onCellClicked = useCallback((cell: Item, event: CellClickedEventArgs) => {
    const cellState : [col: number, row: number] = [cell[0], cell[1]]
    if (appCategory === transportCategory.oil) {
      dispatch(setTicketPageStateOilCell(cellState))
    } else {
      dispatch(setTicketPageStateCell(cellState))
    }
  }, [appCategory, dispatch])

  const onColumnSort = useCallback(
    (sort: GridSort) => {
      setSort(sort);
      if (appCategory === transportCategory.oil) {
        dispatch(setTicketPageStateOilSort(sort))
      } else {
        dispatch(setTicketPageStateSort(sort))
      }
    },
    [appCategory, dispatch]
  );

  const onColumnResize = useCallback(
    (colWidths: (number | undefined)[]) => {
      setColWidths(colWidths);
      if (appCategory === transportCategory.oil) {
        dispatch(setTicketPageStateOilWidths(colWidths));
      } else {
        dispatch(setTicketPageStateWidths(colWidths));
      }
    },
    [appCategory, dispatch]
  );

  return (
    <ThemedPage>
      {() => (
        <ResizeableContent>
          {(size) => (
            <div style={{height: size.height, width: size.width, minWidth: 0}}>
              <TicketWorkspace
                date={new Date(appDttm)}
                onDateChange={onDateChange}
                ticketCount={ttas.length}
                context={driverFocus.notice}
                gridControlsRef={setGridControlsTarget}
                showSearch={showSearch}
                onSearchClick={() => setShowSearch((value) => !value)}
                filterActive={!isDefaultTicketFilter(filter)}
                onFilterClick={filterHandler.open}
                isRefreshing={isFetching}
                onRefresh={() => {refetch(); refetchShifts();}}
                onNewTicket={() => navigate("form")}
                onCompare={() => navigate("compare")}
                onReports={() => navigate("/reports/company-transport-services")}
              >
                <LoadingOverlay visible={isFetching} overlayBlur={2} />
                <TicketDataGrid
                  focusPersonId={driverFocus.personId}
                  onFocusResolved={driverFocus.onResolved}
                  onCommentCreated={refetch}
                  ref={dataEditorRef}
                  controlsTarget={gridControlsTarget}
                  height="100%"
                  showSearch={showSearch}
                  onSearchClose={onSearchClose}
                  onCellClicked={onCellClicked}
                  gridSelection={selection}
                  onGridSelectionChange={setSelection}
                  colWidths={gridState.widths}
                  category={appCategory}
                  shifts={shifts}
                  appDttm={appDttm}
                  onColumnResize={onColumnResize}
                  sort={gridState.sort}
                  onSort={onColumnSort}
                  data={ttas}
                  onItemClick={onItemClick}
                />
              </TicketWorkspace>

              <TicketFilterFormModal
                title={t<string>("common.action.filter", "Filter", {ns: "common"})}
                opened={filterOpened}
                onClose={filterHandler.close}
                onSubmit={onFilterSubmit}
                defaultValues={{
                  ...filter,
                }}
              />
            </div>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
